import type { AgentSubjectType, AgentThreadKind } from '@api/models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteAgentThread,
  getAgentThread,
  listAgentThreads,
  requestAgentReview,
  sendAgentMessage,
  startAgentThread
} from '@website/services/agents.service';

const THREADS_KEY = ['agent-threads'] as const;
/** Shared, so every button that starts a chat can tell one is already starting. */
export const START_ASSISTANT_CHAT_KEY = ['start-assistant-chat'] as const;

export function useAgentThreads(kind: `${AgentThreadKind}`, subject?: { type: `${AgentSubjectType}`; id: string }) {
  return useQuery({
    queryKey: [...THREADS_KEY, kind, subject?.type ?? null, subject?.id ?? null],
    queryFn: () => listAgentThreads(kind, subject),
    staleTime: 15_000
  });
}

export function useAgentThread(id: string) {
  return useQuery({
    queryKey: [...THREADS_KEY, 'one', id],
    queryFn: () => getAgentThread(id),
    retry: false,
    // A new chat is named by its first message: watch for it.
    refetchInterval: (query) => (query.state.data && !query.state.data.title ? 3_000 : false)
  });
}

export function useStartAgentThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title?: string) => startAgentThread(title),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: THREADS_KEY })
  });
}

export function useRequestAgentReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, id }: { type: `${AgentSubjectType}`; id: string }) => requestAgentReview(type, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: THREADS_KEY })
  });
}

/** A new assistant chat that starts on the admin's first message. */
export function useStartAssistantChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: START_ASSISTANT_CHAT_KEY,
    mutationFn: async ({ title, text }: { title: string; text: string }) => {
      const thread = await startAgentThread(title);
      try {
        await sendAgentMessage(thread.id, text, 'asap');
      } catch (error) {
        // An empty chat would only clutter the list.
        await deleteAgentThread(thread.id).catch(() => undefined);
        throw error;
      }
      return thread;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: THREADS_KEY })
  });
}
